import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL } from '../lib/appUrl';
import { ArrowLeft, Check, X, Minus, Sparkles, ArrowRight } from 'lucide-react';

/**
 * A single-page component that renders a comparison against any competitor,
 * driven by the `:slug` URL parameter. Add a new entry to COMPARISONS to add
 * a new comparison page (SEO + conversion tool).
 *
 * Cheapest paid monthly prices, checked 2026-10-20 against official pages.
 * Price cells show only that paid figure (no '~', no free plan). Free plans
 * are called out in a separate note. Canny and Featurebase monthly amounts
 * come from each page's loaded pricing data (yearly is what the screen shows).
 * - Canny Pro from $99/mo (100 tracked users, scales up). https://canny.io/pricing
 * - UserJot Starter $29/mo. https://userjot.com/pricing
 * - Featurebase Growth $37/seat/mo. https://featurebase.app/pricing
 * - Frill Startup $25/mo. https://frill.co/pricing
 * VibeSprint Mini $9/mo.
 */
const COMPARISONS = {
  canny: {
    competitor: 'Canny',
    tagline: 'VibeSprint vs Canny',
    metaSubtitle:
      "Canny is a public voting board for feature requests. VibeSprint is the AI decision layer that tells you which requests are worth building — whether they came from Canny or anywhere else.",
    theirBest: 'Public voting boards where your customers submit ideas',
    ourBest: 'AI-driven Build / Fix / Ignore prioritization with revenue math',
    verdict:
      "They collect. We decide. Best-in-class founders use both: Canny to receive, VibeSprint to prioritize — with plans to import Canny data as a native connector.",
    when_us: [
      "You have feedback from multiple channels (Twitter, Discord, email, Canny) and need one prioritized view.",
      "You want AI-generated revenue impact and ROI/hour estimates on every decision.",
      "You want to know what to IGNORE, not just what's popular.",
      "You care about ROI and effort, not just vote counts.",
    ],
    when_them: [
      'You need public voting and a customer-facing feature portal.',
      'You want dedicated integrations with Intercom, Zendesk, and Slack right now.',
      'You need custom domains, guest posting, and SSO for a large user base.',
    ],
    table: [
      { row: 'AI-powered categorization (Build / Fix / Ignore)', us: true, them: 'partial' },
      { row: 'Revenue impact estimates per decision ($/mo)', us: true, them: false },
      { row: 'ROI/hour calculation', us: true, them: false },
      { row: 'Multi-product / multi-workspace support', us: true, them: 'partial' },
      { row: 'Quick Wins detection', us: true, them: false },
      { row: 'Churn risk auto-flag', us: true, them: false },
      { row: 'Public voting board', us: false, them: true },
      { row: 'Guest / anonymous submissions', us: false, them: true },
      { row: 'Custom domains', us: 'roadmap', them: true },
      { row: 'Native integrations (Intercom, Slack, etc.)', us: 'roadmap', them: true },
      { row: 'SSO', us: 'roadmap', them: true },
      { row: 'Entry paid plan (monthly)', us: '$9/mo', them: 'from $99/mo' },
    ],
    freePlanNote: 'Canny also has a free plan.',
    accentColor: 'bg-orange-500',
  },
  userjot: {
    competitor: 'UserJot',
    tagline: 'VibeSprint vs UserJot',
    metaSubtitle:
      "UserJot is a modern feedback + roadmap + changelog suite for SaaS teams. VibeSprint is the decision-intelligence layer that sits on top of any feedback source — UserJot included — and tells you what to build next.",
    theirBest: 'Integrated feedback board + public roadmap + changelog with AI duplicate detection',
    ourBest: 'AI prioritization with revenue math and Build / Fix / Ignore lanes',
    verdict:
      "UserJot is a beautiful *collection* tool. VibeSprint is a *decision* tool. They complement each other. If you already have a UserJot board, VibeSprint can ingest that feedback and add the ROI/urgency layer on top.",
    when_us: [
      "You already have feedback pouring in and need the AI to prioritize it.",
      "You want $ impact estimates on every roadmap decision.",
      "You care about knowing what to ignore — not just what to organize.",
      "You want a fast, private analysis tool separate from your public-facing roadmap.",
    ],
    when_them: [
      'You need a beautiful public feedback board with voting.',
      'You want a customer-facing changelog with email notifications to voters.',
      'You need an embeddable feedback widget for your app.',
      'You want MCP integration with Claude Code today.',
    ],
    table: [
      { row: 'AI Build / Fix / Ignore lanes', us: true, them: false },
      { row: 'Revenue impact math ($/mo, ROI/hour)', us: true, them: false },
      { row: 'AI duplicate detection', us: 'roadmap', them: true },
      { row: 'Public feedback board with voting', us: false, them: true },
      { row: 'Public roadmap on custom domain', us: 'roadmap', them: true },
      { row: 'Email notifications to voters', us: 'roadmap', them: true },
      { row: 'AI-drafted changelog', us: true, them: true },
      { row: 'Embeddable widget', us: 'roadmap', them: true },
      { row: 'MCP server (Claude, Cursor)', us: true, them: true },
      { row: 'Unlimited users on every plan', us: true, them: true },
      { row: 'Entry paid plan (monthly)', us: '$9/mo', them: '$29/mo' },
    ],
    freePlanNote: 'UserJot also has a free plan.',
    accentColor: 'bg-blue-500',
  },
  featurebase: {
    competitor: 'Featurebase',
    tagline: 'VibeSprint vs Featurebase',
    metaSubtitle:
      "Featurebase is an all-in-one feedback and roadmap platform for SaaS teams. VibeSprint focuses purely on the decision problem — turning noisy feedback into a prioritized backlog with revenue math.",
    theirBest: 'End-to-end feedback + roadmap + changelog with rich integrations',
    ourBest: 'AI-driven prioritization with churn and revenue signals',
    verdict:
      "Featurebase is powerful but heavy. VibeSprint is focused. If your bottleneck is 'what to build next?', we're built for you.",
    when_us: [
      'You want fast, opinionated prioritization instead of another Notion-like database.',
      'You want the AI to have an opinion on urgency and revenue impact.',
      'You want to ship faster, not organize prettier.',
    ],
    when_them: [
      "You need a full customer-facing platform with widgets, portals, and changelog.",
      "You have a team of PMs who want granular customization.",
      "You need Intercom / HubSpot / Zapier integrations today.",
    ],
    table: [
      { row: 'AI Build / Fix / Ignore prioritization', us: true, them: false },
      { row: 'Revenue impact estimates', us: true, them: false },
      { row: 'Multi-workspace for multiple products', us: true, them: true },
      { row: 'Public feedback portal', us: false, them: true },
      { row: 'Widget + embed', us: false, them: true },
      { row: 'Changelog with email notifications', us: 'roadmap', them: true },
      { row: '30+ native integrations', us: 'roadmap', them: true },
      { row: 'Entry paid plan (monthly)', us: '$9/mo', them: '$37/seat/mo' },
    ],
    freePlanNote: 'Featurebase also has a free plan.',
    accentColor: 'bg-emerald-500',
  },
  frill: {
    competitor: 'Frill',
    tagline: 'VibeSprint vs Frill',
    metaSubtitle:
      "Frill is a simple feedback + roadmap portal for small SaaS teams. VibeSprint is a decision engine that tells you what's worth building — not just what people asked for.",
    theirBest: 'Lightweight public feedback + roadmap on a custom domain',
    ourBest: 'AI-driven prioritization with revenue math and churn detection',
    verdict:
      "Frill helps you *display* feedback. VibeSprint helps you *decide* on it. Complementary tools if you want a public portal AND an AI decision layer.",
    when_us: [
      "You care about revenue-driven prioritization more than a pretty public board.",
      'You want AI to filter noise before it hits your roadmap.',
      'You have multiple products and want workspace isolation.',
    ],
    when_them: [
      'You need a public voting portal on a custom domain.',
      'You want a widget your users can click to submit ideas.',
      "You don't need AI — just a clean, cheap feedback board.",
    ],
    table: [
      { row: 'AI Build / Fix / Ignore lanes', us: true, them: false },
      { row: 'Revenue impact and ROI math', us: true, them: false },
      { row: 'Multi-workspace', us: true, them: 'partial' },
      { row: 'Public feedback portal', us: false, them: true },
      { row: 'Custom domain', us: 'roadmap', them: true },
      { row: 'Email notifications', us: 'roadmap', them: true },
      { row: 'Entry paid plan (monthly)', us: '$9/mo', them: '$25/mo' },
    ],
    accentColor: 'bg-purple-500',
  },
};

const Cell = ({ v }) => {
  if (v === true) return <Check className="h-4 w-4 text-primary mx-auto" />;
  if (v === false) return <X className="h-4 w-4 text-muted-foreground mx-auto" />;
  if (v === 'partial') return <Minus className="h-4 w-4 text-orange-500 mx-auto" />;
  if (v === 'roadmap')
    return (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
        soon
      </Badge>
    );
  return <span className="text-sm font-mono">{v}</span>;
};

const ComparisonPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const cmp = COMPARISONS[slug];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = cmp
      ? `${cmp.tagline} | VibeSprint AI`
      : 'Comparison not found | VibeSprint AI';
    return () => {
      document.title = previousTitle;
    };
  }, [cmp]);

  if (!cmp) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Comparison not found</h1>
        <p className="text-muted-foreground mb-6">
          We haven't published a comparison for "{slug}" yet.
        </p>
        <Button onClick={() => navigate('/')} data-testid="comparison-notfound-home">Back to home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid={`comparison-page-${slug}`}>
      {/* Top bar */}
      <div className="border-b bg-card sticky top-0 z-50">
        <div className="container-wide mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="comparison-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={APP_DASHBOARD_URL}>Open dashboard</a>
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 sm:py-20 border-b">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" />
            Comparison
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            {cmp.tagline}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {cmp.metaSubtitle}
          </p>
        </div>
      </section>

      {/* Two-column strengths */}
      <section className="py-14">
        <div className="container mx-auto px-4 max-w-5xl grid md:grid-cols-2 gap-6">
          <Card className="p-6 border-primary/30" data-testid="comparison-us-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-6 w-1 rounded-full bg-primary" />
              <span className="font-semibold text-primary">VibeSprint’s strength</span>
            </div>
            <p className="text-lg font-semibold mb-2">{cmp.ourBest}</p>
            <ul className="text-sm text-muted-foreground space-y-2 mt-4">
              {cmp.when_us.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-6" data-testid="comparison-them-card">
            <div className="flex items-center gap-2 mb-3">
              <div className={`h-6 w-1 rounded-full ${cmp.accentColor}`} />
              <span className="font-semibold text-muted-foreground">{cmp.competitor}’s strength</span>
            </div>
            <p className="text-lg font-semibold mb-2">{cmp.theirBest}</p>
            <ul className="text-sm text-muted-foreground space-y-2 mt-4">
              {cmp.when_them.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-14 bg-muted/30 border-y">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-10">Side-by-side breakdown</h2>
          <Card className="overflow-hidden" data-testid="comparison-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-5 py-3 text-sm font-medium">Feature</th>
                    <th className="px-5 py-3 text-sm font-semibold text-primary min-w-[110px] text-center">VibeSprint</th>
                    <th className="px-5 py-3 text-sm font-semibold min-w-[110px] text-center">{cmp.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {cmp.table.map((r, i) => (
                    <tr key={i} className={i % 2 === 1 ? 'bg-muted/30' : ''}>
                      <td className="px-5 py-3 text-sm">{r.row}</td>
                      <td className="px-5 py-3 text-center"><Cell v={r.us} /></td>
                      <td className="px-5 py-3 text-center"><Cell v={r.them} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="mt-6 flex items-center gap-3 justify-center text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Yes</div>
            <div className="flex items-center gap-1.5"><Minus className="h-3.5 w-3.5 text-orange-500" /> Partial</div>
            <div className="flex items-center gap-1.5"><X className="h-3.5 w-3.5" /> No</div>
            <div className="flex items-center gap-1.5"><Badge variant="outline" className="text-[10px] px-1.5 py-0">soon</Badge> On our roadmap</div>
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="comparison-pricing-as-of">
            Prices checked 2026-10-20, billed monthly.
          </p>
          {cmp.freePlanNote && (
            <p className="mt-1 text-center text-xs text-muted-foreground" data-testid="comparison-free-plan-note">
              {cmp.freePlanNote}
            </p>
          )}
        </div>
      </section>

      {/* Honest verdict */}
      <section className="py-14">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="p-8 border-primary/30 bg-primary/5" data-testid="comparison-verdict">
            <h2 className="text-2xl font-bold mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              The honest verdict
            </h2>
            <p className="text-muted-foreground leading-relaxed">{cmp.verdict}</p>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="text-3xl font-bold mb-3">Try VibeSprint free.</h2>
          <p className="text-muted-foreground mb-6">
            10 analyses per day on the free plan. No signup, no credit card. Load our sample dataset and see the AI in action in 30 seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-cta" size="lg" data-testid="comparison-cta-primary">
              <a href={APP_DASHBOARD_URL}>
                Open dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
            <Button variant="outline" onClick={() => navigate('/pricing')} size="lg" data-testid="comparison-cta-pricing">
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default ComparisonPage;
